import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Image, Skeleton } from 'antd';
import {
  PiCameraBold,
  PiMagnifyingGlassMinus,
  PiMagnifyingGlassPlus,
  PiArrowCounterClockwise,
  PiArrowClockwise,
  PiArrowsHorizontal,
  PiArrowsVertical,
  PiArrowsCounterClockwise,
} from 'react-icons/pi';
import { useAuthImageUrl } from '../hooks/useAuthImageUrl';

interface PhotoViewerProps {
  /** FreePhoto UUID — image is fetched via authenticated backend endpoint. */
  uuid?: string;
  /** Fallback: direct public URL. */
  src?: string;
  alt: string;
}

/**
 * Image viewer using Ant Design's built-in Image component.
 * Provides zoom, rotate, flip and fullscreen preview out of the box.
 * Fetches the photo via JWT-authenticated request when uuid is provided.
 */
export const PhotoViewer: React.FC<PhotoViewerProps> = ({ uuid, src, alt }) => {
  const { blobUrl, loading, error } = useAuthImageUrl(uuid, { stamp: true });

  const resolvedSrc = uuid ? blobUrl : src;

  if (loading) {
    return (
      <UI_LoadingWrapper>
        <Skeleton.Image active style={{ width: '100%', height: '100%' }} />
      </UI_LoadingWrapper>
    );
  }

  if (error || (!loading && !resolvedSrc)) {
    return (
      <UI_ErrorWrapper>
        <PiCameraBold style={{ fontSize: '32px', color: '#999' }} />
        <p>Không thể tải ảnh</p>
      </UI_ErrorWrapper>
    );
  }

  return (
    <UI_Wrapper>
      <GlobalPreviewStyle />
      <Image
        src={resolvedSrc ?? undefined}
        alt={alt}
        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
        preview={{
          toolbarRender: (_, { transform: { scale }, actions: { onZoomOut, onZoomIn, onRotateLeft, onRotateRight, onFlipX, onFlipY, onReset } }) => (
            <UI_Toolbar>
              <UI_ToolbarBtn onClick={onZoomOut} title="Thu nhỏ"><PiMagnifyingGlassMinus style={{ fontSize: '15px' }} /></UI_ToolbarBtn>
              <UI_ToolbarScale>{Math.round(scale * 100)}%</UI_ToolbarScale>
              <UI_ToolbarBtn onClick={onZoomIn} title="Phóng to"><PiMagnifyingGlassPlus style={{ fontSize: '15px' }} /></UI_ToolbarBtn>
              <UI_ToolbarDivider />
              <UI_ToolbarBtn onClick={onRotateLeft} title="Xoay trái"><PiArrowCounterClockwise style={{ fontSize: '15px' }} /></UI_ToolbarBtn>
              <UI_ToolbarBtn onClick={onRotateRight} title="Xoay phải"><PiArrowClockwise style={{ fontSize: '15px' }} /></UI_ToolbarBtn>
              <UI_ToolbarDivider />
              <UI_ToolbarBtn onClick={onFlipX} title="Lật ngang"><PiArrowsHorizontal style={{ fontSize: '15px' }} /></UI_ToolbarBtn>
              <UI_ToolbarBtn onClick={onFlipY} title="Lật dọc"><PiArrowsVertical style={{ fontSize: '15px' }} /></UI_ToolbarBtn>
              <UI_ToolbarDivider />
              <UI_ToolbarBtn onClick={onReset} title="Đặt lại"><PiArrowsCounterClockwise style={{ fontSize: '15px' }} /></UI_ToolbarBtn>
            </UI_Toolbar>
          ),
        }}
      />
    </UI_Wrapper>
  );
};

/** Force the AntD image preview mask to be fully opaque */
const GlobalPreviewStyle = createGlobalStyle`
  .ant-image-preview-mask {
    background-color: #000 !important;
    opacity: 1 !important;
  }
`;

const UI_Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #141414;
  overflow: hidden;

  /* AntD Image wrapper must fill and not overflow */
  .ant-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  /* The actual <img> — contain so full photo is always visible */
  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    cursor: zoom-in;
  }
`;

const UI_LoadingWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #141414;

  .ant-skeleton-image {
    width: 100% !important;
    height: 100% !important;
  }
`;

const UI_ErrorWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #141414;
  color: #666;

  span {
    font-size: 48px;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

const UI_Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 100px;
  padding: 6px 16px;
`;

const UI_ToolbarBtn = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const UI_ToolbarScale = styled.span`
  color: #fff;
  font-size: 13px;
  min-width: 40px;
  text-align: center;
`;

const UI_ToolbarDivider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.25);
  margin: 0 4px;
`;